import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  MapPin, Phone, Mail, Clock, Send, Shield, 
  MessageSquare, CheckCircle, ArrowRight 
} from 'lucide-react';
import { submitContactForm } from '../data/mock';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await submitContactForm(formData);
      if (result.success) {
        toast.success(result.message);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez nous appeler directement.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact | Cabinet CAMBC Conseils Moissac 82</title>
        <meta name="description" content="Contactez CAMBC Conseils, courtier assurance à Moissac. Tél : 05 82 81 91 20. Devis gratuit." />
        <link rel="canonical" href="https://www.cambc-conseils.fr/contact" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F8F6]">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 bg-[#0F2137] overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
              Parlons de votre projet
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 tracking-tight">
              Contactez-nous
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Notre équipe est à votre écoute pour répondre à vos questions et vous accompagner 
              dans vos projets d'assurance.
            </p>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent"></div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-5 gap-12">
              
              {/* Contact Info - Left Side */}
              <div className="lg:col-span-2 space-y-8">
                {/* Quick Contact Card */}
                <Card className="bg-[#0F2137] border-0 rounded-2xl overflow-hidden">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-[#C5A059]/20 flex items-center justify-center">
                        <Shield size={24} className="text-[#C5A059]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">CAMBC Conseils</h3>
                        <p className="text-slate-400 text-sm">Courtier en assurance</p>
                      </div>
                    </div>

                    <div className="space-y-5">
                      <a href="tel:0582819120" className="flex items-center gap-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors group">
                        <div className="w-10 h-10 rounded-lg bg-[#C5A059]/20 flex items-center justify-center">
                          <Phone size={18} className="text-[#C5A059]" />
                        </div>
                        <div>
                          <p className="text-slate-400 text-sm">Téléphone</p>
                          <p className="text-white font-semibold group-hover:text-[#C5A059] transition-colors">05 82 81 91 20</p>
                        </div>
                      </a>

                      <a href="mailto:cabinet@cambc-conseils.fr" className="flex items-center gap-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors group">
                        <div className="w-10 h-10 rounded-lg bg-[#C5A059]/20 flex items-center justify-center">
                          <Mail size={18} className="text-[#C5A059]" />
                        </div>
                        <div>
                          <p className="text-slate-400 text-sm">Email</p>
                          <p className="text-white font-semibold group-hover:text-[#C5A059] transition-colors">cabinet@cambc-conseils.fr</p>
                        </div>
                      </a>

                      <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
                        <div className="w-10 h-10 rounded-lg bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                          <MapPin size={18} className="text-[#C5A059]" />
                        </div>
                        <div>
                          <p className="text-slate-400 text-sm">Adresse</p>
                          <p className="text-white font-semibold">46 bd Pierre Delbrel</p>
                          <p className="text-slate-300">82200 Moissac</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl">
                        <div className="w-10 h-10 rounded-lg bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                          <Clock size={18} className="text-[#C5A059]" />
                        </div>
                        <div>
                          <p className="text-slate-400 text-sm">Horaires</p>
                          <p className="text-white font-semibold">Lun-Ven : 9h-12h / 14h-18h</p>
                          <p className="text-[#C5A059] text-sm mt-1">Souscription 24h/24 en ligne</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Map */}
                <Card className="bg-white border-0 rounded-2xl overflow-hidden shadow-sm">
                  <div className="aspect-[4/3] w-full">
                    <iframe 
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2877.0!2d1.0833!3d44.1056!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDTCsDA2JzIwLjIiTiAxwrAwNScwMC4wIkU!5e0!3m2!1sfr!2sfr!4v1234567890"
                      width="100%" 
                      height="100%" 
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      title="Cabinet CAMBC Conseils - Moissac"
                      className="grayscale hover:grayscale-0 transition-all duration-500"
                    ></iframe>
                  </div>
                </Card>
              </div>

              {/* Contact Form - Right Side */}
              <div className="lg:col-span-3">
                <Card className="bg-white border-0 rounded-2xl shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    {/* Form Header */}
                    <div className="bg-gradient-to-r from-[#0F2137] to-[#1E3A5F] p-8">
                      <div className="flex items-center gap-3 mb-2">
                        <MessageSquare size={24} className="text-[#C5A059]" />
                        <h2 className="text-2xl font-bold text-white">Envoyez-nous un message</h2>
                      </div>
                      <p className="text-slate-300">Nous vous répondons sous 24h ouvrées</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-8 space-y-6">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-[#0F2137] font-medium">Nom complet *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Jean Dupont"
                            className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl py-6"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-[#0F2137] font-medium">Téléphone *</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="06 12 34 56 78"
                            className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl py-6"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#0F2137] font-medium">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="jean.dupont@email.com"
                          className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl py-6"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-[#0F2137] font-medium">Sujet *</Label>
                        <Select onValueChange={(value) => setFormData({...formData, subject: value})}>
                          <SelectTrigger className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl py-6">
                            <SelectValue placeholder="Sélectionnez un sujet" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="devis">Demande de devis</SelectItem>
                            <SelectItem value="info">Demande d'information</SelectItem>
                            <SelectItem value="sinistre">Déclaration de sinistre</SelectItem>
                            <SelectItem value="modification">Modification de contrat</SelectItem>
                            <SelectItem value="resiliation">Résiliation</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-[#0F2137] font-medium">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          placeholder="Décrivez votre demande en quelques lignes..."
                          className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl resize-none"
                        />
                      </div>

                      <div className="flex items-start gap-3 p-4 bg-[#F5EFE6] rounded-xl">
                        <CheckCircle size={20} className="text-[#C5A059] flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-slate-600">
                          Vos données sont traitées de manière confidentielle et ne seront jamais partagées avec des tiers.
                        </p>
                      </div>

                      <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="w-full bg-[#C5A059] hover:bg-[#B08A45] text-white font-semibold py-6 rounded-xl transition-all duration-300 shadow-lg shadow-[#C5A059]/25 hover:shadow-xl"
                      >
                        {isSubmitting ? (
                          'Envoi en cours...'
                        ) : (
                          <>
                            Envoyer le message
                            <Send size={18} className="ml-2" />
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
